import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';

/**
 * Segmented pill control on a light track.
 *
 * Covers every segmented selector in the EMD reference: the R2W..R52W range,
 * the W/M/Q/Y time bucket, Inventory's DOH|WOH and D|W, and the
 * Wholesaler|Material and Sales|Returns switches.
 *
 * Styling follows the app's `tabDesignTwo` pill tabs (pages.component.scss).
 * Exposed as a radiogroup rather than a tablist: most uses pick a value
 * (a range, a metric) and don't control a tab panel.
 */
@Component({
    selector: 'emd-pill-group',
    template: `
    <div
      class="emd-pill-group"
      [class.emd-pill-group--lg]="size === 'lg'"
      role="radiogroup"
      [attr.aria-label]="ariaLabel || null"
    >
      <button
        #pill
        *ngFor="let option of options; let i = index; trackBy: trackByOption"
        type="button"
        role="radio"
        class="emd-pill"
        [class.active]="option === value"
        [attr.aria-checked]="option === value"
        [attr.data-label]="option"
        [disabled]="isDisabled(option)"
        [tabindex]="option === value ? 0 : -1"
        (click)="select(option)"
        (keydown)="onKeydown($event, i)"
      >{{ option }}</button>
    </div>
  `,
    styleUrls: ['./emd-pill-group.component.scss'],
    standalone: false
})
export class EmdPillGroupComponent {
  @ViewChildren('pill') private pills!: QueryList<ElementRef<HTMLButtonElement>>;

  @Input() options: string[] = [];
  @Input() value = '';
  /** `lg` gives the roomier padding used by the tab strip and in-card switches. */
  @Input() size: 'sm' | 'lg' = 'sm';
  @Input() ariaLabel = '';
  @Input() disabledOptions: string[] = [];
  @Output() valueChange = new EventEmitter<string>();

  trackByOption = (_: number, option: string) => option;

  isDisabled(option: string): boolean {
    return this.disabledOptions.includes(option);
  }

  select(option: string): void {
    if (option === this.value || this.isDisabled(option)) { return; }
    this.value = option;
    this.valueChange.emit(option);
  }

  /**
   * Arrow keys move between options, as expected of a radiogroup: only the
   * selected pill is tabbable, so the group is a single tab stop.
   */
  onKeydown(event: KeyboardEvent, index: number): void {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 :
      event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;

    let target: number | null = null;

    if (step !== 0) {
      target = this.nextEnabled(index, step);
    } else if (event.key === 'Home') {
      target = this.nextEnabled(-1, 1);
    } else if (event.key === 'End') {
      target = this.nextEnabled(this.options.length, -1);
    } else {
      return;
    }

    if (target === null) { return; }
    event.preventDefault();
    this.select(this.options[target]);
    this.pills?.get(target)?.nativeElement.focus();
  }

  /** Walks in `step` direction, wrapping, skipping disabled options. */
  private nextEnabled(from: number, step: number): number | null {
    const len = this.options.length;
    if (!len) { return null; }

    for (let i = 1; i <= len; i++) {
      const idx = (((from + step * i) % len) + len) % len;
      if (!this.isDisabled(this.options[idx])) { return idx; }
    }
    return null;
  }
}
